import type { Survey } from '../../types';
import { Logger, ApiErrorHandler } from '../../utils';

export interface EligibilityResponse {
  eligible: boolean;
  message: string;
  survey?: {
    id: string;
    name: string;
    description: string;
    survey_url: string;
    questions: Survey & {
      payout_type?: 'billy' | 'mobile_wallet' | 'cash';
      payout_type_message?: string;
      payout_type_message_title?: string;
    };
    reward_points?: string;
    start_date?: string;
    end_date?: string;
    duration_minutes?: number;
    survey_by?: string;
    project_code?: string;
    status?: string;
    survey_type?: string;
    max_responses_allowed?: number | null;
    created_at?: string;
    updated_at?: string;
    is_active?: boolean;
    current_response_count?: number;
  };
}

export class SurveyEligibilityService {
  private static readonly ELIGIBILITY_ENDPOINT = '/.netlify/functions/survey-eligibility';
  private static readonly DIRECT_API_ENDPOINT = '/api/v1/survey/public/check/eligibility';

  static async checkEligibility(userId: string, surveyId: string, anonymous?: boolean): Promise<EligibilityResponse> {
    const isDevelopment = import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.DEV;
    const useDirectApi = isDevelopment && import.meta.env.VITE_USE_DIRECT_API === 'true';

    try {
      if (useDirectApi) {
        Logger.log('🔍 Using direct backend API (local development)');
        return await this.checkEligibilityDirect(userId, surveyId, anonymous);
      } else {
        Logger.log('🔍 Using secure API proxy (Netlify Functions)');
        return await this.checkEligibilityViaNetlify(userId, surveyId, anonymous);
      }
    } catch (error) {
      Logger.error('❌ Failed to check survey eligibility:', error);
      throw error;
    }
  }

  private static async checkEligibilityViaNetlify(userId: string, surveyId: string, anonymous?: boolean): Promise<EligibilityResponse> {
    const url = new URL(this.ELIGIBILITY_ENDPOINT, window.location.origin);
    url.searchParams.append('userId', userId);
    url.searchParams.append('surveyId', surveyId);
    if (anonymous) {
      url.searchParams.append('anonymous', 'true');
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      await ApiErrorHandler.handleApiError(response, 'Survey Eligibility Check', url.toString());
    }

    const responseJson = await response.json();
    Logger.log('📦 Raw Netlify response:', responseJson);

    // Handle error response: { success: false, error: { code, message } }
    if (responseJson.success === false && responseJson.error) {
      Logger.log('❌ Backend returned error:', responseJson.error);
      return {
        eligible: false,
        message: responseJson.error.message || 'An error occurred checking eligibility'
      };
    }

    // Handle envelope pattern response: { success, data, message }
    const data: EligibilityResponse = responseJson.data ? {
      eligible: responseJson.data.eligible,
      message: responseJson.message || responseJson.data.message || '',
      survey: responseJson.data.survey
    } : responseJson;

    Logger.log('✅ Eligibility check successful');
    return data;
  }

  private static async checkEligibilityDirect(userId: string, surveyId: string, anonymous?: boolean): Promise<EligibilityResponse> {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL_DEV || import.meta.env.VITE_API_BASE_URL_PROD || 'http://localhost:8080';
    const apiAuthToken = import.meta.env.VITE_API_AUTH_TOKEN;

    const url = new URL(this.DIRECT_API_ENDPOINT, apiBaseUrl);
    // Backend expects snake_case parameters
    url.searchParams.append('user_id', userId);
    url.searchParams.append('survey_id', surveyId);
    if (anonymous) {
      url.searchParams.append('anonymous', 'true');
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (apiAuthToken) {
      headers['Authorization'] = `Bearer ${apiAuthToken}`;
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      await ApiErrorHandler.handleApiError(response, 'Survey Eligibility Check', url.toString());
    }

    const responseJson = await response.json();
    Logger.log('📦 Raw API response:', responseJson);

    // Handle error response: { success: false, error: { code, message } }
    if (responseJson.success === false && responseJson.error) {
      Logger.log('❌ Backend returned error:', responseJson.error);
      return {
        eligible: false,
        message: responseJson.error.message || 'An error occurred checking eligibility'
      };
    }

    // Handle envelope pattern response: { success, data, message }
    const data: EligibilityResponse = responseJson.data ? {
      eligible: responseJson.data.eligible,
      message: responseJson.message || responseJson.data.message || '',
      survey: responseJson.data.survey
    } : responseJson;

    Logger.log('✅ Eligibility check successful (direct)');
    return data;
  }


}
